import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Paper,
  Alert,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  Dialog,
  IconButton,
  TextField,
  MenuItem,
  Button
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { compatibilityMatrix, areConnectorsCompatible } from '../services/compatibilityMatrix';
import { cableImageCaptureService } from '../services/cableImageCaptureService';
import clientImageCaptureService from '../services/clientImageCaptureService';
import html2canvas from 'html2canvas';
import { generateEndToEndImage, captureCableConfiguration } from '../services/localSvgGenerator';
// Removed html-to-image import - now using EC2 system for image generation
import { getAvailableWireTypes, getDefaultWireType, wireTypes, getDefaultWireTypeForConnectors, getWireTypeWarning } from '../services/wireCompatibilityService';
import { getConnectorPairCompatibility } from '../services/utilityService';
import { generateSKU } from '../services/skuService';
import { formatGenderWithXlrHint } from '../utils/connectorDisplayUtils';
import { buildConnectorFamilies, getFamilyDescription } from '../services/connectorFamiliesService';
import { API_ENDPOINTS } from '../config';
import WarningIcon from '@mui/icons-material/Warning';
import ConnectorCompatibilityIndicator from './ConnectorCompatibilityIndicator';
import ConnectorImageZoom from './ConnectorImageZoom';


// Universal helper to show compatibility warnings/notes
function CompatibilityWarning({ compatibilityResult, connectorA, connectorB }) {
  const [result, setResult] = React.useState(compatibilityResult);
  
  // Helper function to map connector to matrix ID
  const mapToMatrixId = (connector, configuration = 'standard') => {
    if (!connector) return undefined;
    
    // If it's already a string ID, return as is (might already be mapped)
    if (typeof connector === 'string') return connector;
    
    // Handle specific connector IDs from the UI
    const connectorId = connector.id;
    
    // PowerCON Blue variants
    if (connectorId === 'NAC3FXXA-W-S' || connectorId === 'NAC3MPXXA') {
      return connectorId; // Already in correct format
    }
    
    // PowerCON Grey variants - map UI IDs to matrix IDs
    if (connectorId === 'PowerCon_Grey_cable_mount_male') {
      return 'NAC3FXXB-W-S';
    }
    if (connectorId === 'PowerCon_Grey_panel_mount_female') {
      return 'NAC3MPXXB';
    }
    
    // TRUE1 variants
    if (connectorId === 'NAC3M-TRUE1-S' || connectorId === 'NAC3MPX-TOP' || 
        connectorId === 'NAC3F-TRUE1-S' || connectorId === 'NAC3FPX-TOP') {
      return connectorId; // Already in correct format
    }
    
    // IEC connectors - handle both simple and full IDs
    if (connector.family === 'IEC') {
      if (connectorId === 'IEC_C14') {
        if (configuration === 'panel-mount') return 'IEC_C14_panel_mount_male';
        return 'IEC_C14_cable_mount_male';
      } else if (connectorId === 'IEC_C13') {
        if (configuration === 'panel-mount') return 'IEC_C13_panel_mount_female';
        return 'IEC_C13_cable_mount_female';
      }
    }
    
    // Handle full IEC IDs
    if (connectorId === 'IEC_C14_cable_mount_male' || connectorId === 'IEC_C14_panel_mount_male' ||
        connectorId === 'IEC_C13_cable_mount_female' || connectorId === 'IEC_C13_panel_mount_female') {
      return connectorId; // Already in correct format
    }
    
    // NEMA 5-15 connectors - handle both simple and full IDs
    if (connector.family === 'NEMA 5-15') {
      if (connectorId === 'NEMA_5-15P') {
        if (configuration === 'panel-mount') return 'NEMA_5-15P_panel_mount_male';
        return 'NEMA_5-15P_cable_mount_male';
      } else if (connectorId === 'NEMA_5-15R') {
        if (configuration === 'panel-mount') return 'NEMA_5-15R_panel_mount_female';
        return 'NEMA_5-15R_cable_mount_female';
      }
    }
    
    // Handle full NEMA IDs
    if (connectorId === 'NEMA_5-15P_cable_mount_male' || connectorId === 'NEMA_5-15P_panel_mount_male' ||
        connectorId === 'NEMA_5-15R_cable_mount_female' || connectorId === 'NEMA_5-15R_panel_mount_female') {
      return connectorId; // Already in correct format
    }
    
    // Default: return connector.id
    return connector.id;
  };
  
  React.useEffect(() => {
    if (connectorA && connectorB) {
      // Map connector objects to matrix IDs
      const connectorAId = mapToMatrixId(connectorA, connectorA?.configuration || 'standard');
      const connectorBId = mapToMatrixId(connectorB, connectorB?.configuration || 'standard');
      const res = areConnectorsCompatible(connectorAId, connectorBId);
      setResult(res);
    } else {
      setResult(compatibilityResult);
    }
  }, [connectorA, connectorB, compatibilityResult]);
  
  if (!result) return null;
  
  // Handle incompatibility (compatible: false)
  if (result.compatible === false) {
    return (
      <Alert severity="error" icon={<WarningIcon />} sx={{ mt: 1, mb: 1 }}>
        {result.reason && (
          <Typography variant="body2" fontWeight="bold">{result.reason}</Typography>
        )}
        {result.details && (
          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>{result.details}</Typography>
        )}
      </Alert>
    );
  }
  
  // Handle compatibility warnings and notes
  const { warning, notes } = result;
  if (!warning && !notes) return null;
  
  return (
    <Alert severity="warning" icon={<WarningIcon />} sx={{ mt: 1, mb: 1 }}>
      {warning && (
        <Typography variant="body2" fontWeight="bold">{warning}</Typography>
      )}
      {notes && (
        <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>{notes}</Typography>
      )}
    </Alert>
  );
}
export { CompatibilityWarning };

// --- NEW MULTI-END SELECTOR START ---

function BaseConnectorSelector({
  value = {},
  onChange,
  type = 'audio',
  showCompatibility = true,
  onReady = null
}) {
  // State for showing tooltip notifications
  const [tooltipMessage, setTooltipMessage] = useState('');
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [tooltipAnchor, setTooltipAnchor] = useState(null);
  // State for both ends
  const [connectors, setConnectors] = useState({
    endA: value.endA || {},
    endB: value.endB || {}
  });
  // Which end's modal is open for selection
  const [modalOpen, setModalOpen] = useState(null); // 'A' or 'B' or null
  // Track selected family for modal
  const [selectedFamily, setSelectedFamily] = useState(null);
  
  // Wire selection state
  const [wireType, setWireType] = useState(value.wireType || 'PSM');
  const [availableWireTypes, setAvailableWireTypes] = useState([]);
  
  // Special instructions state
  const [specialInstructions, setSpecialInstructions] = useState(value.specialInstructions || '');
  
  // Track last onChange call to prevent duplicates
  const lastOnChangeRef = useRef(null);
  
  // Track previous wire type to prevent unnecessary onChange calls
  const prevWireTypeRef = useRef(wireType);
  
  // Track capture state to prevent repeated captures
  const [captureCompleted, setCaptureCompleted] = useState(false);
  const [captureAttempts, setCaptureAttempts] = useState(0);
  const MAX_CAPTURE_ATTEMPTS = 3;

  // Mobile detection for capture system
  const [isMobile, setIsMobile] = useState(false);
  const [deviceInfo, setDeviceInfo] = useState({});

  // Detect mobile device and capture environment
  useEffect(() => {
    const detectMobileEnvironment = () => {
      const userAgent = navigator.userAgent;
      const isIPad = /iPad/i.test(userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      const isAndroid = /Android/i.test(userAgent);
      const isIPhone = /iPhone|iPod/i.test(userAgent);
      const isMobileUA = /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
      const isSmallScreen = window.innerWidth <= 1024;
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      
      const mobile = isSmallScreen || isMobileUA || isIPad;
      
      const deviceInfo = {
        isMobile: mobile,
        isTouchDevice,
        isIPad,
        isAndroid,
        isIPhone,
        screenWidth: window.innerWidth,
        screenHeight: window.innerHeight,
        userAgent,
        maxTouchPoints: navigator.maxTouchPoints
      };
      
      setIsMobile(mobile);
      setDeviceInfo(deviceInfo);
      
      console.log('📱 [MOBILE-DETECT] BaseConnectorSelector device detection:', deviceInfo);
    };
    
    detectMobileEnvironment();
    
    // Re-detect on resize
    const handleResize = () => detectMobileEnvironment();
    window.addEventListener('resize', handleResize);
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Families for this cable type
  const families = useMemo(() => {
    try {
      const result = buildConnectorFamilies(type);
      console.log(`Built ${result.length} families for type: ${type}`, result);
      return result;
    } catch (error) {
      console.error('Error building connector families:', error);
      return [];
    }
  }, [type]);

  // Helper function to map configuration to mount type
  const mapConfigurationToMountType = useCallback((configuration) => {
    switch (configuration) {
      case 'standard':
        return 'cable';
      case 'panel-mount':
        return 'panel';
      case 'right-angle':
        return 'rightangle'; // Note: some connectors use 'right-angle', others use 'rightangle'
      default:
        return configuration;
    }
  }, []);

  // Helper function to find the correct connector variant based on configuration
  const findConnectorVariant = useCallback((currentConnector, configuration) => {
    if (!currentConnector || !currentConnector.connector) return currentConnector;
    
    // Get the current connector's data from the compatibility matrix
    const currentConnectorData = compatibilityMatrix[currentConnector.connector];
    if (!currentConnectorData) return currentConnector;
    
    const { family, type, purpose, gender, poles } = currentConnectorData;
    const targetMountType = mapConfigurationToMountType(configuration);
    

    

    
    // Search through the compatibility matrix to find the variant with the same family, type, purpose, gender, and poles
    // but with the desired mount type (configuration)
    for (const [connectorId, connectorData] of Object.entries(compatibilityMatrix)) {
      // Handle comma-separated purposes like 'audio, hybrid' or 'audio,hybrid'
      const connectorPurposes = connectorData.purpose ? 
        connectorData.purpose.split(',').map(p => p.trim().toLowerCase()) : 
        [];
      
      const targetPurposes = purpose ? 
        purpose.split(',').map(p => p.trim().toLowerCase()) : 
        [];
      
      // Check if any of the connector's purposes match any of the target purposes
      const purposeMatches = connectorPurposes.some(connectorPurpose => 
        targetPurposes.includes(connectorPurpose)
      );
      
      if (connectorData.family === family && 
          connectorData.type === type &&
          purposeMatches &&
          connectorData.gender === gender &&
          connectorData.poles === poles &&
          (connectorData.mountType === targetMountType || 
           (targetMountType === 'rightangle' && connectorData.mountType === 'right-angle'))) {
        

        

        // Return the complete connector object with the new ID
        return {
          ...currentConnector,
          connector: connectorId,
          skuCode: connectorData.skuCode || connectorId, // Use skuCode from matrix, fallback to id
          configuration: configuration,
          image: connectorData.image,
          description: connectorData.description,
          gender: connectorData.gender, // Update gender if it changed
          mountType: connectorData.mountType // Update mountType
        };
      }
    }
    

    
    // If no variant found, return the original connector with updated configuration
    return {
      ...currentConnector,
      configuration: configuration
    };
  }, [mapConfigurationToMountType]);



  // Update local state when value prop changes
  useEffect(() => {
    // Only update if value.endA or value.endB changed (shallow compare for key properties)
    setConnectors(prev => {
      const currentEndA = prev.endA;
      const currentEndB = prev.endB;
      const newEndA = value.endA || {};
      const newEndB = value.endB || {};
      
      // Compare key properties instead of full JSON.stringify
      const sameA = currentEndA.connector === newEndA.connector && 
                   currentEndA.configuration === newEndA.configuration;
      const sameB = currentEndB.connector === newEndB.connector && 
                   currentEndB.configuration === newEndB.configuration;
      
      if (sameA && sameB) return prev;
      
      // Reset the lastOnChangeRef when value changes significantly
      lastOnChangeRef.current = null;
      
      // Reset capture state when connectors change significantly
      setCaptureCompleted(false);
      setCaptureAttempts(0);
      
      return {
        endA: newEndA,
        endB: newEndB
      };
    });
  }, [value.endA, value.endB]);



  // Helper function to check if a configuration would be incompatible with the other end
  const checkConfigurationCompatibility = useCallback((end, configuration) => {
    const currentConnector = connectors[end === 'A' ? 'endA' : 'endB'];
    if (!currentConnector?.connector) return null;
    
    // First check if this configuration actually exists for this connector
    const currentConnectorData = compatibilityMatrix[currentConnector.connector];
    if (!currentConnectorData) return null;
    
    const { family, type, purpose, poles } = currentConnectorData;
    const targetMountType = mapConfigurationToMountType(configuration);
    
    // Check if a variant with this configuration actually exists
    let variantExists = false;
    for (const [connectorId, connectorData] of Object.entries(compatibilityMatrix)) {
      // Handle comma-separated purposes like 'audio, hybrid' or 'audio,hybrid'
      const connectorPurposes = connectorData.purpose ? 
        connectorData.purpose.split(',').map(p => p.trim().toLowerCase()) : 
        [];
      
      const targetPurposes = purpose ? 
        purpose.split(',').map(p => p.trim().toLowerCase()) : 
        [];
      
      // Check if any of the connector's purposes match any of the target purposes
      const purposeMatches = connectorPurposes.some(connectorPurpose => 
        targetPurposes.includes(connectorPurpose)
      );
      
      if (connectorData.family === family && 
          connectorData.type === type &&
          purposeMatches &&
          connectorData.poles === poles &&
          (connectorData.mountType === targetMountType || 
           (targetMountType === 'rightangle' && connectorData.mountType === 'right-angle'))) {
        variantExists = true;
        break;
      }
    }
    
    // If the configuration doesn't exist, return null (will be disabled, not red)
    if (!variantExists) return null;
    
    // Find the connector variant for this configuration
    const newConnector = findConnectorVariant(currentConnector, configuration);
    if (!newConnector) return null;
    
    // Check compatibility with the other end
    const otherEnd = end === 'A' ? connectors.endB : connectors.endA;
    if (!otherEnd?.connector) return null;
    
    // For the other end, we need to consider the configuration variant
    let otherId = otherEnd.connector || otherEnd.id;
    
    // If the other end has a configuration that's different from 'standard',
    // we need to find the variant connector ID
    if (otherEnd.configuration && otherEnd.configuration !== 'standard') {
      const variantConnector = findConnectorVariant(otherEnd, otherEnd.configuration);
      if (variantConnector && variantConnector.connector) {
        otherId = variantConnector.connector;
      }
    }
    
    const compatResult = areConnectorsCompatible(otherId, newConnector.connector);
    
    if (!compatResult) {
      // If areConnectorsCompatible returns null, it means no compatibility rule exists = incompatible
      return {
        incompatible: true,
        reason: 'Not compatible with selected connector'
      };
    }
    
    if (compatResult && !compatResult.compatible) {
      return {
        incompatible: true,
        reason: compatResult.reason || compatResult.details || 'Incompatible with selected connector'
      };
    }
    
    return { incompatible: false };
  }, [connectors, findConnectorVariant, mapConfigurationToMountType]);

  // Helper function to get available configurations for a specific end
  const getConfigurationsForEnd = useCallback((end) => {
    const currentConnector = connectors[end === 'A' ? 'endA' : 'endB'];
    if (!currentConnector?.connector) {
      return { standard: true, 'right-angle': true, 'panel-mount': true };
    }
    
    // Get the connector data from the compatibility matrix
    const connectorData = compatibilityMatrix[currentConnector.connector];
    if (!connectorData) {
      return { standard: true, 'right-angle': true, 'panel-mount': true };
    }
    
    const { family, type, purpose, gender, poles } = connectorData;
    
    // Search through the compatibility matrix to find all variants with the same family, type, purpose, gender, AND poles
    const availableMountTypes = new Set();
    
    Object.values(compatibilityMatrix).forEach(connector => {
      // Handle comma-separated purposes like 'audio, hybrid' or 'audio,hybrid'
      const connectorPurposes = connector.purpose ? 
        connector.purpose.split(',').map(p => p.trim().toLowerCase()) : 
        [];
      
      const targetPurposes = purpose ? 
        purpose.split(',').map(p => p.trim().toLowerCase()) : 
        [];
      
      // Check if any of the connector's purposes match any of the target purposes
      const purposeMatches = connectorPurposes.some(connectorPurpose => 
        targetPurposes.includes(connectorPurpose)
      );
      
      if (connector.family === family && 
          connector.type === type && 
          purposeMatches &&
          connector.gender === gender &&
          connector.poles === poles) {
        availableMountTypes.add(connector.mountType);
      }
    });

    // Map mountType values to configuration names
    const hasStandard = availableMountTypes.has('cable');
    const hasRightAngle = availableMountTypes.has('rightangle') || availableMountTypes.has('right-angle');
    const hasPanelMount = availableMountTypes.has('panel');

    return {
      standard: hasStandard,
      'right-angle': hasRightAngle,
      'panel-mount': hasPanelMount
    };
  }, [connectors]);

  // Helper: open modal for a given end
  const handleOpenModal = useCallback((end) => {
    setSelectedFamily(null);
    setModalOpen(end);
  }, []);
  
  // Helper: close modal
  const handleCloseModal = useCallback(() => {
    setModalOpen(null);
    setSelectedFamily(null);
  }, []);

  // Mobile-aware capture function for BaseConnectorSelector
  const captureMobileAwareImage = useCallback(async (sku) => {
    console.log('📱 [MOBILE-CAPTURE] Starting mobile-aware capture for BaseConnectorSelector...');
    console.log('📱 [MOBILE-CAPTURE] Device info:', deviceInfo);
    
    try {
      // Generate unique filename - always desktop quality for BigCommerce
      const timestamp = Date.now();
      const sourceIndicator = isMobile ? '_from_mobile' : '_from_desktop';
      const outputFileName = `${sku}_desktop_quality${sourceIndicator}_${timestamp}.png`;
      
      // Get the current DOM content of the BaseConnectorSelector
      const selectorElement = document.querySelector('[data-testid="base-connector-selector"]') || 
                             document.querySelector('.MuiPaper-root') ||
                             document.body;
      
      if (!selectorElement) {
        console.error('❌ [MOBILE-CAPTURE] BaseConnectorSelector element not found');
        return null;
      }
      
      // Prepare HTML content with DESKTOP-optimized styling for consistent product images
      // Even if source is mobile, we generate desktop-quality HTML for BigCommerce
      const htmlContent = `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="utf-8">
            <meta name="viewport" content="width=1200, initial-scale=1.0">
            <title>Base Connector Selector - Desktop Quality (Source: ${isMobile ? 'Mobile' : 'Desktop'})</title>
            <style>
              body {
                margin: 0;
                padding: 20px;
                background: white;
                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
                font-size: 16px;
                line-height: 1.5;
              }
              
              .desktop-capture-container {
                max-width: 800px;
                margin: 0 auto;
                padding: 20px;
                background: white;
                border-radius: 8px;
                box-shadow: 0 2px 8px rgba(0,0,0,0.1);
              }
              
              .connector-display {
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: center;
                gap: 30px;
                margin: 20px 0;
                padding: 20px;
                background: #f8f9fa;
                border-radius: 8px;
              }
              
              .connector-box {
                width: 80px;
                height: 80px;
                border: 2px solid #4caf50;
                border-radius: 8px;
                display: flex;
                align-items: center;
                justify-content: center;
                background: #e8f5e8;
                overflow: hidden;
              }
              
              .connector-box img {
                width: 70px;
                height: 70px;
                object-fit: contain;
              }
              
              .wire-visualization {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 200px;
                position: relative;
                padding: 10px 0;
              }
              
              .wire-segment {
                width: 60px;
                height: 6px;
                background: linear-gradient(90deg, #4a4a4a 0%, #666 50%, #4a4a4a 100%);
                border-radius: 3px;
                box-shadow: 0 2px 4px rgba(0,0,0,0.2);
              }
              
              .wire-arrow {
                width: 0;
                height: 0;
                border-left: 6px solid #b32b2b;
                border-top: 4px solid transparent;
                border-bottom: 4px solid transparent;
                margin: 0 5px;
                filter: drop-shadow(0 1px 2px rgba(0,0,0,0.2));
              }
              
              .connector-labels {
                display: flex;
                gap: 30px;
                margin-top: 10px;
                align-items: center;
                justify-content: center;
                width: 100%;
              }
              
              .connector-label {
                width: 80px;
                text-align: center;
                font-size: 14px;
                font-weight: 500;
                color: #333;
              }
              
              .wire-label {
                width: 200px;
                text-align: center;
                font-size: 12px;
                color: #666;
              }
              
              .config-info {
                margin-top: 15px;
                padding: 15px;
                background: #f5f5f5;
                border-radius: 6px;
                font-size: 14px;
              }
              
              .config-row {
                display: flex;
                justify-content: space-between;
                margin-bottom: 8px;
              }
              
              .config-label {
                font-weight: 500;
                color: #333;
              }
              
              .config-value {
                color: #666;
              }
              
              .source-indicator {
                position: absolute;
                top: 10px;
                right: 10px;
                background: rgba(0,0,0,0.7);
                color: white;
                padding: 4px 8px;
                border-radius: 4px;
                font-size: 10px;
                font-weight: 500;
              }
            </style>
          </head>
          <body>
            <div class="desktop-capture-container">
              ${isMobile ? '<div class="source-indicator">📱 Mobile Source</div>' : ''}
              <h2 style="text-align: center; margin-bottom: 20px; color: #333;">
                ${type.charAt(0).toUpperCase() + type.slice(1)} Cable Configuration
              </h2>
              
              <div class="connector-display">
                <div class="connector-box">
                  ${connectors.endA?.image ? 
                    `<img src="${connectors.endA.image}" alt="${connectors.endA.description || 'End A'}" />` : 
                    '<div style="font-size: 24px; font-weight: bold; color: #666;">A</div>'
                  }
                </div>
                
                <div class="wire-visualization">
                  <div class="wire-segment"></div>
                  <div class="wire-arrow"></div>
                  <div class="wire-segment"></div>
                </div>
                
                <div class="connector-box">
                  ${connectors.endB?.image ? 
                    `<img src="${connectors.endB.image}" alt="${connectors.endB.description || 'End B'}" />` : 
                    '<div style="font-size: 24px; font-weight: bold; color: #666;">B</div>'
                  }
                </div>
              </div>
              
              <div class="connector-labels">
                <div class="connector-label">
                  ${connectors.endA?.description || 'Select End A...'}
                </div>
                <div class="wire-label">
                  ${wireType || 'PSM'} Wire
                </div>
                <div class="connector-label">
                  ${connectors.endB?.description || 'Select End B...'}
                </div>
              </div>
              
              ${(connectors.endA?.connector || connectors.endB?.connector) ? `
                <div class="config-info">
                  <div class="config-row">
                    <span class="config-label">Wire Type:</span>
                    <span class="config-value">${wireType || 'PSM'}</span>
                  </div>
                  ${connectors.endA?.configuration ? `
                    <div class="config-row">
                      <span class="config-label">End A Config:</span>
                      <span class="config-value">${connectors.endA.configuration}</span>
                    </div>
                  ` : ''}
                  ${connectors.endB?.configuration ? `
                    <div class="config-row">
                      <span class="config-label">End B Config:</span>
                      <span class="config-value">${connectors.endB.configuration}</span>
                    </div>
                  ` : ''}
                  ${specialInstructions ? `
                    <div class="config-row">
                      <span class="config-label">Special Instructions:</span>
                      <span class="config-value">${specialInstructions}</span>
                    </div>
                  ` : ''}
                </div>
              ` : ''}
            </div>
          </body>
        </html>
      `;
      
      console.log('🖥️ [DESKTOP-CAPTURE] Prepared desktop-quality HTML content (source device:', isMobile ? 'mobile' : 'desktop', ')');
      
      // Call the server-side capture endpoint with mobile-aware parameters
      const response = await fetch('https://nlfx-cable.luknai.com/api/capture-svg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          htmlContent: htmlContent,
          outputFileName: outputFileName,
          captureType: 'base-connector-mobile',
          contentType: 'html',
          deviceInfo: deviceInfo,
          isMobile: isMobile
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [MOBILE-CAPTURE] Server capture failed:', errorText);
        return null;
      }
      
      const result = await response.json();
      console.log('✅ [DESKTOP-CAPTURE] Desktop-quality capture successful (source:', isMobile ? 'mobile' : 'desktop', '):', result.s3Url);
      
      return result.s3Url;
      
    } catch (error) {
      console.error('❌ [MOBILE-CAPTURE] Mobile-aware capture error:', error);
      return null;
    }
  }, [connectors, wireType, specialInstructions, type, isMobile, deviceInfo]);

  // Expose capture function for external use (e.g., ReviewCart component)
  useEffect(() => {
    if (onReady) {
      onReady(!!(connectors.endA?.connector && connectors.endB?.connector), captureMobileAwareImage);
    }
  }, [connectors.endA?.connector, connectors.endB?.connector, onReady, captureMobileAwareImage]);

  // Helper function to prevent duplicate onChange calls
  const safeOnChange = useCallback((updatedConnectors) => {
    const key = JSON.stringify({ ...updatedConnectors, wireType, specialInstructions });
    if (lastOnChangeRef.current === key) {
      return;
    }
    lastOnChangeRef.current = key;
    if (onChange) onChange({ ...updatedConnectors, wireType, specialInstructions });
  }, [onChange, wireType, specialInstructions]);

  // Helper: clear connector for a specific end
  const handleClearConnector = useCallback((end) => {
    setConnectors(prev => {
      const updated = { ...prev, [end === 'A' ? 'endA' : 'endB']: {} };
      safeOnChange(updated);
      return updated;
    });
  }, [safeOnChange]);

  // Handle family select in modal
  const handleFamilySelect = useCallback((family) => {
    setSelectedFamily(family.id);
  }, []);

  // Handle connector select in modal
  const handleConnectorSelect = useCallback((connector) => {
    // Check compatibility before allowing selection
    const otherEnd = modalOpen === 'A' ? connectors.endB : connectors.endA;
    if (otherEnd?.connector || otherEnd?.id) {
      const candidateId = connector.id;
      
      // For the other end, we need to consider the configuration variant
      let otherId = otherEnd.connector || otherEnd.id;
      
      // If the other end has a configuration that's different from 'standard',
      // we need to find the variant connector ID
      if (otherEnd.configuration && otherEnd.configuration !== 'standard') {
        const variantConnector = findConnectorVariant(otherEnd, otherEnd.configuration);
        if (variantConnector && variantConnector.connector) {
          otherId = variantConnector.connector;
        }
      }
      
      // Important: compatibilityMatrix isn't consistently authored bidirectionally.
      // Always check in the same direction as the rest of this component (other -> candidate),
      // and rely on areConnectorsCompatible being symmetric as a safety net.
      const compatResult = areConnectorsCompatible(otherId, candidateId);
      
      // Prevent selection if incompatible
      if (compatResult && !compatResult.compatible) {
        // You could show a toast/alert here instead of silently preventing
        console.warn('Incompatible connector selection prevented:', compatResult.reason || compatResult.details || 'Unknown reason');
        return; // Don't proceed with selection
      }
    }

    // Map mountType from compatibility matrix to configuration value
    let autoConfiguration = 'standard';
    if (connector.mountType === 'rightangle' || connector.mountType === 'right-angle') {
      autoConfiguration = 'right-angle';
    } else if (connector.mountType === 'panel') {
      autoConfiguration = 'panel-mount';
    } else if (connector.mountType === 'cable') {
      autoConfiguration = 'standard';
    }
    
    // Compose new connector object using data from compatibility matrix
    const newConn = {
      brand: 'Professional',
      connector: connector.id,
      gender: connector.gender,
      variant: autoConfiguration,
      skuCode: connector.skuCode || connector.id, // Use skuCode from matrix, fallback to id
      description: connector.name,
      image: connector.image,
      configuration: autoConfiguration,
      family: connector.family,
      type: connector.type,
      mountType: connector.mountType,
      poles: connector.poles,
      purpose: connector.purpose
    };
    
    // Update state
    setConnectors(prev => {
      const updated = { ...prev, [modalOpen === 'A' ? 'endA' : 'endB']: newConn };
      safeOnChange(updated);
      return updated;
    });
    handleCloseModal();
  }, [modalOpen, connectors.endA, connectors.endB, safeOnChange, findConnectorVariant, handleCloseModal]);

  // Helper function to check compatibility for a connector in the modal
  const checkConnectorCompatibility = useCallback((connector) => {
    const otherEnd = modalOpen === 'A' ? connectors.endB : connectors.endA;
    if (!otherEnd?.connector && !otherEnd?.id) {
      return { status: 'compatible', message: 'Compatible' };
    }
    
    try {
      // Use the same approach as other connector selectors - direct ID access
      const candidateId = connector.id;
      
      // For the other end, we need to consider the configuration variant
      // If the other end has a configuration, we need to find the correct variant ID
      let otherId = otherEnd.connector || otherEnd.id;
      
      // If the other end has a configuration that's different from 'standard',
      // we need to find the variant connector ID
      if (otherEnd.configuration && otherEnd.configuration !== 'standard') {
        const variantConnector = findConnectorVariant(otherEnd, otherEnd.configuration);
        if (variantConnector && variantConnector.connector) {
          otherId = variantConnector.connector;
        }
      }
      
      const compatibilityResult = areConnectorsCompatible(otherId, candidateId);
      
      if (!compatibilityResult) {
        return { status: 'incompatible', message: 'Not compatible' };
      }
      
      if (compatibilityResult.compatible === false) {
        return { 
          status: 'incompatible', 
          message: compatibilityResult.reason || 'Incompatible',
          notes: compatibilityResult.details || null
        };
      }
      
      // Check wire gauge compatibility if both connectors are power connectors
      if (connector.purpose === 'power' && otherEnd.purpose === 'power') {
        try {
          // This function is no longer imported, so this block will cause an error.
          // Assuming it's meant to be removed or replaced with a new import if needed.
          // For now, commenting out to avoid immediate errors.
          // const wireGaugeWarning = getConnectorCompatibilityWarningSync(candidateId, otherId);
          // if (wireGaugeWarning) {
          //   return {
          //     status: 'warning',
          //     message: wireGaugeWarning.message || compatibilityResult.warning || 'Compatible with wire gauge considerations',
          //     notes: wireGaugeWarning.details || wireGaugeWarning.message
          //   };
          // }
        } catch (wireGaugeError) {
          console.warn('Error checking wire gauge compatibility:', wireGaugeError);
        }
      }
      
      if (compatibilityResult.warning) {
        return { 
          status: 'warning', 
          message: compatibilityResult.warning,
          notes: compatibilityResult.notes 
        };
      }
      
      return { status: 'compatible', message: 'Compatible' };
    } catch (error) {
      console.warn('Error checking compatibility:', error);
      return { status: 'incompatible', message: 'Error checking compatibility' };
    }
  }, [modalOpen, connectors.endA, connectors.endB, findConnectorVariant]);

  // Handle configuration change for an end
  const handleConfigurationChange = useCallback((end, newConfiguration, event) => {
    setConnectors(prev => {
      const conn = prev[end];
      if (!conn) return prev;
      
      // Find the new connector variant based on the new configuration
      const updatedConn = findConnectorVariant(conn, newConfiguration);
      
      // Check compatibility with the other end using the compatibility matrix
      const otherEnd = end === 'endA' ? prev.endB : prev.endA;
      if (otherEnd?.connector || otherEnd?.id) {
        // For the other end, we need to consider the configuration variant
        let otherId = otherEnd.connector || otherEnd.id;
        
        // If the other end has a configuration that's different from 'standard',
        // we need to find the variant connector ID
        if (otherEnd.configuration && otherEnd.configuration !== 'standard') {
          const variantConnector = findConnectorVariant(otherEnd, otherEnd.configuration);
          if (variantConnector && variantConnector.connector) {
            otherId = variantConnector.connector;
          }
        }
        
        const compatResult = areConnectorsCompatible(otherId, updatedConn.connector);
        
        // Prevent configuration change if incompatible and show tooltip
        if (compatResult && !compatResult.compatible) {
          const message = compatResult.reason || compatResult.details || 'Incompatible with selected connector';
          setTooltipMessage(`Configuration change prevented: ${message}`);
          setTooltipAnchor(event?.currentTarget);
          setTooltipOpen(true);
          
          // Auto-hide tooltip after 3 seconds
          setTimeout(() => {
            setTooltipOpen(false);
          }, 3000);
          
          return prev; // Don't update the state
        }
      }
      

      const updated = { ...prev, [end]: updatedConn };
      safeOnChange(updated);
      return updated;
    });
  }, [safeOnChange, findConnectorVariant]);

  // Compatibility check
  const compatResult = useMemo(() => {
    if (connectors.endA && connectors.endB && (connectors.endA.connector || connectors.endA.id) && (connectors.endB.connector || connectors.endB.id)) {
      // For both ends, we need to consider the configuration variants
      let idA = connectors.endA.connector || connectors.endA.id;
      let idB = connectors.endB.connector || connectors.endB.id;
      
      // If endA has a configuration that's different from 'standard',
      // we need to find the variant connector ID
      if (connectors.endA.configuration && connectors.endA.configuration !== 'standard') {
        const variantConnector = findConnectorVariant(connectors.endA, connectors.endA.configuration);
        if (variantConnector && variantConnector.connector) {
          idA = variantConnector.connector;
        }
      }
      
      // If endB has a configuration that's different from 'standard',
      // we need to find the variant connector ID
      if (connectors.endB.configuration && connectors.endB.configuration !== 'standard') {
        const variantConnector = findConnectorVariant(connectors.endB, connectors.endB.configuration);
        if (variantConnector && variantConnector.connector) {
          idB = variantConnector.connector;
        }
      }
      
      const baseResult = areConnectorsCompatible(idA, idB);
      
      // Add wire gauge compatibility checking for power connectors
      if (baseResult && baseResult.compatible && connectors.endA.purpose === 'power' && connectors.endB.purpose === 'power') {
        try {
          // This function is no longer imported, so this block will cause an error.
          // Assuming it's meant to be removed or replaced with a new import if needed.
          // For now, commenting out to avoid immediate errors.
          // const wireGaugeWarning = getConnectorCompatibilityWarningSync(idA, idB);
          // if (wireGaugeWarning) {
          //   return {
          //     status: 'warning',
          //     message: wireGaugeWarning.message || baseResult.warning || 'Compatible with wire gauge considerations',
          //     notes: wireGaugeWarning.details || wireGaugeWarning.message
          //   };
          // }
        } catch (wireGaugeError) {
          console.warn('Error checking wire gauge compatibility:', wireGaugeError);
        }
      }
      
      // Convert the baseResult to the expected format
      if (!baseResult) {
        // If areConnectorsCompatible returns null, it means no compatibility rule exists = incompatible
        return {
          status: 'incompatible',
          message: 'Not compatible',
          notes: null
        };
      }
      
      if (baseResult.compatible === false) {
        return {
          status: 'incompatible',
          message: baseResult.reason || 'Incompatible',
          notes: baseResult.details || null
        };
      } else if (baseResult.warning) {
        return {
          status: 'warning',
          message: baseResult.warning,
          notes: baseResult.notes
        };
      } else {
        return {
          status: 'compatible',
          message: 'Compatible'
        };
      }
    }
    return null;
  }, [connectors, findConnectorVariant]);

  // Sync wireType state with value prop - FIXED: Remove wireType from dependencies to prevent circular dependency
  useEffect(() => {
    if (value.wireType && value.wireType !== wireType) {
      setWireType(value.wireType);
    }
  }, [value.wireType]); // Removed wireType from dependencies

  // Update available wire types when connectors change - FIXED: Remove wireType from dependencies
  useEffect(() => {
    if (connectors.endA?.connector && connectors.endB?.connector) {
      // Get wire types compatible with both connectors
      const endAWires = getAvailableWireTypes(connectors.endA.connector);
      const endBWires = getAvailableWireTypes(connectors.endB.connector);
      
      // Find intersection of compatible wire types
      const endAWireIds = endAWires.map(wire => wire.id);
      const endBWireIds = endBWires.map(wire => wire.id);
      const commonWireIds = endAWireIds.filter(id => endBWireIds.includes(id));
      
      // Filter out 'Zip' from available wire types since it's determined by cable style
      const filteredWireIds = commonWireIds.filter(id => id !== 'Zip');
      
      const commonWires = filteredWireIds.map(id => wireTypes[id]).filter(Boolean);
      setAvailableWireTypes(commonWires);
      
      // Set default wire type only when current selection is invalid or not set; otherwise respect user's manual choice
      setWireType(currentWireType => {
        // For DMX cables, ALWAYS use PSDMX wire type
        if (type === 'dmx' && filteredWireIds.includes('PSDMX')) {
          return 'PSDMX';
        }
        // If the user has already chosen a wire type from the dropdown and it's still valid, keep it
        if (currentWireType && filteredWireIds.includes(currentWireType)) {
          return currentWireType;
        }
        // No valid selection (connectors changed or initial load): apply smart default
        const connectorIds = [connectors.endA.connector, connectors.endB.connector];
        const smartDefaultWire = getDefaultWireTypeForConnectors(connectorIds, { cableType: type });
        if (smartDefaultWire && filteredWireIds.includes(smartDefaultWire)) {
          return smartDefaultWire;
        }
        if (commonWires.length > 0) {
          const preferred = type === 'audio' && filteredWireIds.includes('PSM')
            ? 'PSM'
            : commonWires[0].id;
          return preferred;
        }
        return currentWireType;
      });
    } else {
      setAvailableWireTypes([]);
    }
  }, [connectors.endA?.connector, connectors.endB?.connector, type]); // Added type to dependencies for DMX cable check

  // Watch for wire type changes and notify parent
  useEffect(() => {
    // Only call onChange if wire type actually changed and we have connectors
    if (wireType && wireType !== prevWireTypeRef.current && (connectors.endA?.connector || connectors.endB?.connector)) {
      console.log('🔍 BaseConnectorSelector wireType changed from', prevWireTypeRef.current, 'to', wireType, 'calling onChange');
      prevWireTypeRef.current = wireType;
      safeOnChange({ endA: connectors.endA, endB: connectors.endB, wireType, specialInstructions });
    }
  }, [wireType, connectors.endA, connectors.endB, specialInstructions, safeOnChange]);

  // Handle wire type change - FIXED: Use functional state update to avoid stale closures
  const handleWireTypeChange = useCallback((event) => {
    const newWireType = event.target.value;
    console.log('🔍 BaseConnectorSelector handleWireTypeChange:', newWireType);
    setWireType(newWireType);
    
    // Use functional state update to get current connectors
    setConnectors(currentConnectors => {
      const key = JSON.stringify({ endA: currentConnectors.endA, endB: currentConnectors.endB, wireType: newWireType, specialInstructions });
      if (lastOnChangeRef.current === key) {
        return currentConnectors;
      }
      lastOnChangeRef.current = key;
      console.log('🔍 BaseConnectorSelector calling onChange with wireType:', newWireType);
      if (onChange) onChange({ endA: currentConnectors.endA, endB: currentConnectors.endB, wireType: newWireType, specialInstructions });
      return currentConnectors;
    });
  }, [onChange, specialInstructions]);

  // Handle special instructions change
  const handleInstructionsChange = useCallback((event) => {
    const newInstructions = event.target.value;
    setSpecialInstructions(newInstructions);
    
    // Use functional state update to get current connectors
    setConnectors(currentConnectors => {
      const key = JSON.stringify({ endA: currentConnectors.endA, endB: currentConnectors.endB, wireType, specialInstructions: newInstructions });
      if (lastOnChangeRef.current === key) {
        return currentConnectors;
      }
      lastOnChangeRef.current = key;
      if (onChange) onChange({ endA: currentConnectors.endA, endB: currentConnectors.endB, wireType, specialInstructions: newInstructions });
      return currentConnectors;
    });
  }, [onChange, wireType]);

  // Handle reset - clear all selections
  const handleReset = useCallback(() => {
    setConnectors({ endA: {}, endB: {} });
    setWireType('PSM'); // Default fallback
    setSelectedFamily(null);
    setModalOpen(null);
    setAvailableWireTypes([]);
    setCaptureCompleted(false); // Reset capture state
    setCaptureAttempts(0); // Reset capture attempts
    setSpecialInstructions(''); // Reset special instructions
    
    // Call onChange with reset values
    if (onChange) onChange({ endA: {}, endB: {}, wireType: 'PSM', specialInstructions: '' });
  }, [onChange]);

  // Auto-capture image when ready and Ready state (both ends selected and configured)
  // DISABLED: Auto-capture moved to ReviewCart to ensure proper timing with length selection
  useEffect(() => {
    const performAutoCapture = async () => {
      // DISABLED: Don't auto-capture here, let ReviewCart handle it with complete data
      return;
      
      const ready = connectors.endA?.connector && 
                   connectors.endB?.connector && 
                   connectors.endA?.configuration && 
                   connectors.endB?.configuration &&
                   compatResult?.status !== 'incompatible';
      
      // Only log occasionally to reduce spam
      const shouldLog = !captureCompleted || Math.random() < 0.1; // Log 10% of the time after first capture
      if (shouldLog) {
        console.log('🚨 CRITICAL: BaseConnectorSelector auto-capture check:', {
          ready,
          captureCompleted,
          endA: !!connectors.endA?.connector,
          endB: !!connectors.endB?.connector,
          configA: !!connectors.endA?.configuration,
          configB: !!connectors.endB?.configuration,
          compatStatus: compatResult?.status
        });
      }
      
      // Auto-capture when ready and not already captured, with attempt limit
      if (ready && !captureCompleted && captureAttempts < MAX_CAPTURE_ATTEMPTS) {
        console.log(`🔴 [AUTO-CAPTURE] BaseConnectorSelector is ready, attempting client-side capture first... (attempt ${captureAttempts + 1}/${MAX_CAPTURE_ATTEMPTS})`);
        setCaptureAttempts(prev => prev + 1);
        
        // Debounce multiple calls to prevent redundant captures
        if (window.baseCaptureInProgress) {
          console.log('⚠️ [AUTO-CAPTURE] Capture already in progress, skipping...');
          return;
        }
        window.baseCaptureInProgress = true;

        try {
          await new Promise(r => setTimeout(r, 500)); // Wait for render
          
          // Generate SKU first (needed for both client-side and server-side fallback)
          const sku = generateSKU({ 
            style: 'standard',
            connectors: { endA: connectors.endA.connector, endB: connectors.endB.connector },
            wireType: wireType
          });
          
          // Try client-side capture first (more accurate)
          const s3Url = await clientImageCaptureService.captureAndUploadImage('base-connector-selector', sku, Date.now());
          
          if (s3Url) {
            console.log('✅ [AUTO-CAPTURE] BaseConnectorSelector client-side capture succeeded:', s3Url);
            
            // Mark as completed on successful capture
            setCaptureCompleted(true);
            
            // Store the S3 image URL in the config if onChange is available
            if (onChange) {
              onChange({ 
                endA: connectors.endA, 
                endB: connectors.endB, 
                wireType: wireType,
                specialInstructions: specialInstructions,
                imageUrl: s3Url 
              });
            }
          } else {
            console.error('❌ [AUTO-CAPTURE] BaseConnectorSelector client-side capture failed');
            
            // Fallback to local generation since EC2 server is not available
            console.log('🔄 [AUTO-CAPTURE] Falling back to local generation...');
            
            // Try DOM capture first (more reliable), then fallback to SVG generation
            let localResult;
            
            try {
              console.log('🎯 [AUTO-CAPTURE] Attempting DOM capture of cable configuration...');
              const pngData = await captureCableConfiguration();
              
              if (pngData) {
                console.log('✅ [AUTO-CAPTURE] DOM capture successful, PNG data length:', pngData.length);
                localResult = {
                  success: true,
                  imageData: pngData,
                  svgData: null
                };
              } else {
                throw new Error('DOM capture returned no data');
              }
            } catch (captureError) {
              console.warn('⚠️ [AUTO-CAPTURE] DOM capture failed, falling back to SVG generation:', captureError);
              
              // Fallback to SVG generation
              localResult = await generateEndToEndImage({
                connectors: {
                  endA: connectors.endA,
                  endB: connectors.endB
                },
                length: 15, // Default length for standard cables (in feet)
                wireType: wireType,
                specialInstructions: specialInstructions,
                unit: 'ft'
              });
            }
            
            if (localResult.success) {
              console.log('✅ [AUTO-CAPTURE] BaseConnectorSelector local image generation successful');
              
              // Upload the locally generated image to S3
              try {
                const uploadResponse = await fetch(API_ENDPOINTS.UPLOAD_DIAGRAM, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    sku,
                    imageData: localResult.imageData,
                    contentType: 'image/png'
                  })
                });
                
                if (uploadResponse.ok) {
                  const uploadData = await uploadResponse.json();
                  const s3ImageUrl = uploadData.url;
                  console.log('✅ [AUTO-CAPTURE] BaseConnectorSelector uploaded to S3:', s3ImageUrl);
                  
                  // Mark as completed on successful upload
                  setCaptureCompleted(true);
                  
                  // Store the S3 image URL in the config if onChange is available
                  if (onChange) {
                    onChange({ 
                      endA: connectors.endA, 
                      endB: connectors.endB, 
                      wireType: wireType,
                      specialInstructions: specialInstructions,
                      imageUrl: s3ImageUrl 
                    });
                  }
                } else {
                  const uploadError = await uploadResponse.text();
                  console.warn('⚠️ [AUTO-CAPTURE] Failed to upload BaseConnectorSelector image to S3:', uploadError);
                }
              } catch (uploadError) {
                console.error('❌ [AUTO-CAPTURE] Error uploading BaseConnectorSelector image to S3:', uploadError);
              }
            } else {
              console.error(`❌ [AUTO-CAPTURE] BaseConnectorSelector local image generation failed (attempt ${captureAttempts}/${MAX_CAPTURE_ATTEMPTS}):`, localResult.error);
              
              // Only reset if we haven't reached max attempts
              if (captureAttempts >= MAX_CAPTURE_ATTEMPTS) {
                console.error('❌ [AUTO-CAPTURE] Max capture attempts reached, giving up');
                setCaptureCompleted(true); // Mark as completed to stop retrying
              }
            }
          }
        } catch (error) {
          console.error(`❌ [AUTO-CAPTURE] BaseConnectorSelector capture error (attempt ${captureAttempts}/${MAX_CAPTURE_ATTEMPTS}):`, error);
          
          // Only reset if we haven't reached max attempts
          if (captureAttempts >= MAX_CAPTURE_ATTEMPTS) {
            console.error('❌ [AUTO-CAPTURE] Max capture attempts reached, giving up');
            setCaptureCompleted(true); // Mark as completed to stop retrying
          }
        } finally {
          // Always clear the capture flag, regardless of success or failure
          window.baseCaptureInProgress = false;
        }
      }
      
      if (onReady) {
        onReady(!!ready);
      }
    };
    
    performAutoCapture();
  }, [connectors.endA?.connector, connectors.endA?.configuration, connectors.endB?.connector, connectors.endB?.configuration, compatResult?.status, onReady, wireType, captureCompleted, captureAttempts, MAX_CAPTURE_ATTEMPTS]);

  // Auto-reset incompatible configurations when other end changes - FIXED: Stabilize dependencies
  useEffect(() => {
    if (connectors.endA?.connector && connectors.endA?.configuration) {
      const configCompat = checkConfigurationCompatibility('A', connectors.endA.configuration);
      if (configCompat?.incompatible) {
        // Reset to standard configuration if current is incompatible
        handleConfigurationChange('endA', 'standard');
      }
    }
    if (connectors.endB?.connector && connectors.endB?.configuration) {
      const configCompat = checkConfigurationCompatibility('B', connectors.endB.configuration);
      if (configCompat?.incompatible) {
        // Reset to standard configuration if current is incompatible
        handleConfigurationChange('endB', 'standard');
      }
    }
  }, [connectors.endA?.connector, connectors.endA?.configuration, connectors.endB?.connector, connectors.endB?.configuration, checkConfigurationCompatibility, handleConfigurationChange]);

  // --- UI ---
  return (
    <Box sx={{ mt: 2 }} data-testid="base-connector-selector">
      {/* Header with Reset button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6" component="h2">
          Configure {type.charAt(0).toUpperCase() + type.slice(1)} Connectors
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            onClick={handleReset} 
            variant="outlined" 
            sx={{ 
              fontSize: '0.8rem',
              px: 2,
              py: 0.5
            }}
          >
            RESET
          </Button>

        </Box>
      </Box>
      
      {/* Summary row with clickable ends */}
      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
        <Paper elevation={1} sx={{ p: 2, backgroundColor: '#f8f9fa', mb: 3, maxWidth: 600, mx: 'auto', width: '100%' }}>
          {/* Removed: <Typography variant="subtitle2" color="text.secondary" gutterBottom align="center">Current Selection</Typography> */}
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: { xs: 1, sm: 2 }, width: '100%' }}>
            {/* End A */}
            <Box sx={{ 
              cursor: 'pointer', 
              width: { xs: 50, sm: 70 }, 
              height: { xs: 50, sm: 70 }, 
              border: connectors.endA?.connector ? 
                (compatResult?.status === 'incompatible' ? '2px solid #f44336' : 
                 compatResult?.status === 'warning' ? '2px solid #ffb300' : 
                 '2px solid #4caf50') : 
                '2px solid #4caf50', 
              borderRadius: 2, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              backgroundColor: connectors.endA?.connector ? 
                (compatResult?.status === 'incompatible' ? '#ffebee' : 
                 compatResult?.status === 'warning' ? '#fff8e1' : 
                 '#e8f5e8') : 
                '#f5f5f5', 
              overflow: 'hidden' 
            }} onClick={() => handleOpenModal('A')}>
              {connectors.endA?.image ? (
                <ConnectorImageZoom key={`endA-${connectors.endA.connector}-${connectors.endA.configuration}`} src={connectors.endA.image} alt={connectors.endA.description || 'End A'} style={{ width: { xs: '40px', sm: '60px' }, height: { xs: '40px', sm: '60px' }, objectFit: 'contain' }} />
              ) : (
                <Typography variant="h6" color="text.secondary" fontWeight="bold" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>A</Typography>
              )}
            </Box>
            {/* Wire visualization */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: { xs: 100, sm: 152 }, position: 'relative', py: 1 }}>
              <Box sx={{ width: { xs: 40, sm: 60 }, height: { xs: 4, sm: 8 }, background: 'linear-gradient(90deg, #4a4a4a 0%, #666 50%, #4a4a4a 100%)', borderRadius: 4, boxShadow: '0 2px 4px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)', position: 'relative' }} />
              <Box sx={{ width: 0, height: 0, borderLeft: { xs: '3px solid #b32b2b', sm: '6px solid #b32b2b' }, borderTop: { xs: '2px solid transparent', sm: '4px solid transparent' }, borderBottom: { xs: '2px solid transparent', sm: '4px solid transparent' }, ml: 0.25, filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.2))' }} />
              <Box sx={{ width: { xs: 40, sm: 60 }, height: { xs: 4, sm: 8 }, background: 'linear-gradient(90deg, #4a4a4a 0%, #666 50%, #4a4a4a 100%)', borderRadius: 4, boxShadow: '0 2px 4px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)', position: 'relative' }} />
            </Box>
            {/* End B */}
            <Box sx={{ 
              cursor: 'pointer', 
              width: { xs: 50, sm: 70 }, 
              height: { xs: 50, sm: 70 }, 
              border: connectors.endB?.connector ? 
                (compatResult?.status === 'incompatible' ? '2px solid #f44336' : 
                 compatResult?.status === 'warning' ? '2px solid #ffb300' : 
                 '2px solid #4caf50') : 
                '2px solid #4caf50', 
              borderRadius: 2, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              backgroundColor: connectors.endB?.connector ? 
                (compatResult?.status === 'incompatible' ? '#ffebee' : 
                 compatResult?.status === 'warning' ? '#fff8e1' : 
                 '#e8f5e8') : 
                '#f5f5f5', 
              overflow: 'hidden' 
            }} onClick={() => handleOpenModal('B')}>
              {connectors.endB?.image ? (
                <ConnectorImageZoom key={`endB-${connectors.endB.connector}-${connectors.endB.configuration}`} src={connectors.endB.image} alt={connectors.endB.description || 'End B'} style={{ width: { xs: '40px', sm: '60px' }, height: { xs: '40px', sm: '60px' }, objectFit: 'contain' }} />
              ) : (
                <Typography variant="h6" color="text.secondary" fontWeight="bold" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>B</Typography>
              )}
            </Box>
          </Box>
          {/* Descriptions: on mobile allow flex + wrap so long names don't overflow */}
          <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, mt: 1, alignItems: 'center', justifyContent: 'center', width: '100%', flexWrap: 'wrap' }}>
            <Typography variant="caption" sx={{ flex: { xs: '1 1 0', sm: 'none' }, minWidth: 0, width: { sm: 70 }, textAlign: 'center', fontSize: { xs: '0.7rem', sm: '0.75rem' }, wordBreak: 'break-word' }} fontWeight="500">
              {connectors.endA?.description || 'Select End A...'}
            </Typography>
            <Box sx={{ width: { xs: 0, sm: 152 }, flexShrink: 0, display: { xs: 'none', sm: 'block' } }} />
            <Typography variant="caption" sx={{ flex: { xs: '1 1 0', sm: 'none' }, minWidth: 0, width: { sm: 70 }, textAlign: 'center', fontSize: { xs: '0.7rem', sm: '0.75rem' }, wordBreak: 'break-word' }} fontWeight="500">
              {connectors.endB?.description || 'Select End B...'}
            </Typography>
          </Box>
          
          {/* Warning message when there's a compatibility warning */}
          {compatResult?.status === 'warning' && compatResult?.message && (
            <Box sx={{ mt: 1, textAlign: 'center' }}>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: '#f57c00', 
                  fontWeight: '500',
                  backgroundColor: '#fff3e0',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  border: '1px solid #ffb300',
                  display: 'inline-block'
                }}
              >
                ⚠️ {compatResult.warning || compatResult.reason}
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>

      {/* Compatibility indicator */}
      {showCompatibility && connectors.endA?.connector && connectors.endB?.connector && (
        <Box sx={{ mb: 2 }}>
          <ConnectorCompatibilityIndicator result={compatResult} />
        </Box>
      )}

      {/* Wire Type Selection */}
      {connectors.endA?.connector && connectors.endB?.connector && availableWireTypes.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Paper elevation={1} sx={{ p: 2, backgroundColor: '#f8f9fa' }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Wire Type Selection
            </Typography>
            <TextField
              select
              value={wireType}
              onChange={handleWireTypeChange}
              size="small"
              fullWidth
              variant="outlined"
              label="Select Wire Type"
              disabled={availableWireTypes.length === 0}
              sx={{ maxWidth: 300 }}
            >
              {availableWireTypes.map((wire) => (
                <MenuItem key={wire.id} value={wire.id}>
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      {wire.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {wire.description}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </TextField>
            
            {/* Wire Type Warning */}
            {(() => {
              const connectorIds = [connectors.endA.connector, connectors.endB.connector];
              const warning = getWireTypeWarning(wireType, connectorIds);
              
              if (warning) {
                return (
                  <Alert 
                    severity="info" 
                    sx={{ 
                      mt: 2,
                      backgroundColor: '#e3f2fd', 
                      border: '1px solid #90caf9',
                      '& .MuiAlert-icon': { color: '#1976d2' }
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      {warning}
                    </Typography>
                  </Alert>
                );
              }
              return null;
            })()}
          </Paper>
        </Box>
      )}

      {/* Wire Type Compatibility Warning - Only show for non-power connectors */}
      {(() => {
        // Check if either connector is power-related
        const isEndAPower = connectors.endA?.purpose?.includes('power');
        const isEndBPower = connectors.endB?.purpose?.includes('power');
        
        // Only show warning if both connectors are selected, no common wire types, and neither is power-related
        const shouldShowWarning = connectors.endA?.connector && connectors.endB?.connector && 
                                 availableWireTypes.length === 0 && 
                                 !isEndAPower && !isEndBPower;
        
        // Debug logging for development (can be removed in production)
        // console.log('BaseConnectorSelector Wire Type Debug:', { 
        //   endA: connectors.endA?.connector, 
        //   endB: connectors.endB?.connector, 
        //   availableWireTypes: availableWireTypes.length,
        //   isEndAPower,
        //   isEndBPower,
        //   shouldShowWarning 
        // });
        
        return shouldShowWarning;
      })() && (
        <Box sx={{ mb: 2 }}>
          <Alert 
            severity="warning" 
            sx={{ 
              backgroundColor: '#fff3cd', 
              border: '1px solid #ffeaa7',
              '& .MuiAlert-icon': { color: '#856404' }
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
              Wire Type Selection Not Available
            </Typography>
            <Typography variant="body2" color="text.secondary">
              The selected connectors don't have compatible wire types. This may be because:
            </Typography>
            <Box component="ul" sx={{ mt: 1, mb: 1, pl: 2 }}>
              <Typography component="li" variant="body2" color="text.secondary">
                The connectors require different wire specifications (e.g., 4-conductor vs 8-conductor cable)
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                One or both connectors don't support wire type selection
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                The connector types are incompatible for this wire selection method
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              For audio cables, ensure both connectors support the same wire type (PSM, PSI, Zip, or speaker cable). Consider selecting different connectors or proceeding to the next step where alternative wire selection may be available.
            </Typography>
          </Alert>
        </Box>
      )}

      {/* Special Instructions */}
      <TextField
        label="Special Instructions (Optional)"
        multiline
        rows={3}
        fullWidth
        value={specialInstructions}
        onChange={handleInstructionsChange}
        sx={{ mb: 2 }}
        placeholder="How are you going to use this cable? We evaluate every cable we build... Please DO NOT include any personal information!"
      />

      {/* Configuration selectors for both ends */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {['A', 'B'].map((end) => {
          const conn = connectors[end === 'A' ? 'endA' : 'endB'];
          if (!conn?.connector) return null;
          return (
            <Grid item xs={12} sm={6} key={end}>
              <Paper elevation={1} sx={{ 
                p: 2, 
                backgroundColor: 'grey.50',
                border: compatResult?.status === 'incompatible' ? '2px solid #f44336' : 
                        compatResult?.status === 'warning' ? '2px solid #ffb300' : 
                        '1px solid #e0e0e0',
                overflow: 'hidden',
                minWidth: 0
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, gap: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ minWidth: 0, wordBreak: 'break-word', flex: 1 }}>
                    <strong>End {end}:</strong> {conn.description || 'Unknown Connector'}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => handleClearConnector(end)}
                    sx={{ 
                      color: '#d32f2f',
                      backgroundColor: 'rgba(211, 47, 47, 0.1)',
                      flexShrink: 0,
                      '&:hover': {
                        backgroundColor: 'rgba(211, 47, 47, 0.2)',
                        color: '#b71c1c'
                      },
                      width: 24,
                      height: 24
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2, mb: 2, minWidth: 0 }}>
                  <Box sx={{ width: { xs: 40, sm: 60 }, height: { xs: 40, sm: 60 }, flexShrink: 0 }}>
                    <ConnectorImageZoom key={`detail-${end}-${conn.connector || 'unknown'}-${conn.configuration || 'standard'}`} src={conn.image || ''} alt={conn.description || 'Connector'} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{conn.description || 'Unknown Connector'}</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                      <Chip label={`Type: ${conn.family || 'Unknown'}`} size="small" />
                      <Chip label={`Gender: ${formatGenderWithXlrHint(conn)}`} size="small" />
                    </Box>
                  </Box>
                </Box>
                {/* Configuration Selector */}
                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontSize: '0.8rem' }}>
                    Configuration:
                  </Typography>
                  <ToggleButtonGroup
                    value={conn.configuration || 'standard'}
                    exclusive
                    onChange={(e, newConfig) => {
                      if (newConfig !== null) {
                        // Check if this configuration would be incompatible
                        const configCompat = checkConfigurationCompatibility(end, newConfig);
                        if (configCompat?.incompatible) {
                          // Don't allow the change, just show tooltip
                          setTooltipMessage(`Configuration change prevented: ${configCompat.reason}`);
                          setTooltipAnchor(e?.currentTarget);
                          setTooltipOpen(true);
                          
                          // Auto-hide tooltip after 3 seconds
                          setTimeout(() => {
                            setTooltipOpen(false);
                          }, 3000);
                          return;
                        }
                        handleConfigurationChange(end === 'A' ? 'endA' : 'endB', newConfig, e);
                      }
                    }}
                    aria-label={`connector configuration for end ${end}`}
                    size="small"
                    sx={{
                      width: '100%',
                      display: 'flex',
                      gap: 1,
                      mt: 1,
                      '& .MuiToggleButton-root': {
                        flex: 1,
                        fontSize: '0.9rem',
                        py: 1.2,
                        borderRadius: 2,
                        minWidth: 0,
                        border: '1px solid',
                        borderColor: 'divider',
                        transition: 'all 0.15s',
                        '&.Mui-selected': {
                          backgroundColor: '#e8f5e8',
                          color: '#2e7d32',
                          border: '1.5px solid #4caf50',
                          fontWeight: 600,
                          boxShadow: '0 2px 8px 0 rgba(76,175,80,0.08)',
                          '&:hover': { backgroundColor: '#dcedc8' }
                        },
                        '&.Mui-selected.incompatible': {
                          backgroundColor: '#ffebee',
                          color: '#c62828',
                          border: '2px solid #f44336',
                          fontWeight: 600,
                          boxShadow: '0 2px 8px 0 rgba(244,67,54,0.08)',
                          '&:hover': { backgroundColor: '#ffcdd2' }
                        },
                        '&:hover': {
                          backgroundColor: '#f5f5f5',
                          color: '#388e3c',
                          borderColor: '#bdbdbd'
                        }
                      }
                    }}
                  >
                    {(() => {
                      const standardCompat = checkConfigurationCompatibility(end, 'standard');
                      const rightAngleCompat = checkConfigurationCompatibility(end, 'right-angle');
                      const panelMountCompat = checkConfigurationCompatibility(end, 'panel-mount');
                      
                      return (
                        <>
                          <Tooltip 
                            title={
                              !getConfigurationsForEnd(end).standard
                                ? "Configuration not available for this connector type"
                                : standardCompat?.incompatible 
                                  ? (standardCompat.reason || 'Incompatible')
                                  : "Standard straight connector"
                            } 
                            arrow
                          >
                            <ToggleButton 
                              value="standard" 
                              aria-label="standard"
                              disabled={!getConfigurationsForEnd(end).standard}
                              className={standardCompat?.incompatible ? 'incompatible' : ''}
                              sx={{
                                ...(standardCompat?.incompatible && {
                                  backgroundColor: '#ffebee',
                                  color: '#c62828',
                                  border: '2px solid #f44336',
                                  '&:hover': {
                                    backgroundColor: '#ffcdd2'
                                  }
                                })
                              }}
                            >
                              STD
                            </ToggleButton>
                          </Tooltip>
                          <Tooltip 
                            title={
                              !getConfigurationsForEnd(end)['right-angle']
                                ? "Configuration not available for this connector type"
                                : rightAngleCompat?.incompatible 
                                  ? (rightAngleCompat.reason || 'Incompatible')
                                  : "Right Angle (90°) connector"
                            } 
                            arrow
                          >
                            <ToggleButton 
                              value="right-angle" 
                              aria-label="right angle" 
                              disabled={!getConfigurationsForEnd(end)['right-angle']}
                              className={rightAngleCompat?.incompatible ? 'incompatible' : ''}
                              sx={{
                                ...(rightAngleCompat?.incompatible && {
                                  backgroundColor: '#ffebee',
                                  color: '#c62828',
                                  border: '2px solid #f44336',
                                  '&:hover': {
                                    backgroundColor: '#ffcdd2'
                                  }
                                }),
                                ...(rightAngleCompat?.incompatible && conn.configuration === 'right-angle' && {
                                  '&.Mui-selected': {
                                    backgroundColor: '#ffebee',
                                    color: '#c62828',
                                    border: '2px solid #f44336',
                                    fontWeight: 600,
                                    boxShadow: '0 2px 8px 0 rgba(244,67,54,0.08)',
                                    '&:hover': { backgroundColor: '#ffcdd2' }
                                  }
                                })
                              }}
                            >
                              RA
                            </ToggleButton>
                          </Tooltip>
                          <Tooltip 
                            title={
                              !getConfigurationsForEnd(end)['panel-mount']
                                ? "Configuration not available for this connector type"
                                : panelMountCompat?.incompatible 
                                  ? (panelMountCompat.reason || 'Incompatible')
                                  : "Panel mount connector"
                            }
                            arrow
                          >
                            <ToggleButton 
                              value="panel-mount" 
                              aria-label="panel mount"
                              disabled={!getConfigurationsForEnd(end)['panel-mount']}
                              className={panelMountCompat?.incompatible ? 'incompatible' : ''}
                              sx={{
                                ...(panelMountCompat?.incompatible && {
                                  backgroundColor: '#ffebee',
                                  color: '#c62828',
                                  border: '2px solid #f44336',
                                  '&:hover': {
                                    backgroundColor: '#ffcdd2'
                                  }
                                })
                              }}
                            >
                              PNL
                            </ToggleButton>
                          </Tooltip>
                        </>
                      );
                    })()}
                  </ToggleButtonGroup>
                </Box>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {/* Modal for connector selection (shared for both ends) */}
      <Dialog 
        open={!!modalOpen} 
        onClose={handleCloseModal} 
        maxWidth="sm" 
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            maxHeight: '90vh',
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'contain',
            touchAction: 'pan-y'
          }
        }}
      >
        <Box sx={{ 
          p: 2,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain',
          touchAction: 'pan-y'
        }}>
          <Typography variant="h6" gutterBottom>
            Select Connector for End {modalOpen}
          </Typography>
          
          {/* Compatibility explanation when other end is selected */}
          {((modalOpen === 'A' && connectors.endB?.connector) || (modalOpen === 'B' && connectors.endA?.connector)) && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Compatibility Check Active:</strong> Showing compatibility with your selected {modalOpen === 'A' ? 'End B' : 'End A'} connector. 
                Green = Compatible, Yellow = Warning, Red = Incompatible
              </Typography>
            </Alert>
          )}
          
          {!selectedFamily ? (
            <Grid container spacing={2}>
              {families.length > 0 ? (
                families.map((family) => (
                  <Grid item xs={12} sm={6} key={family.id}>
                    <Card sx={{ cursor: 'pointer', height: '100%', '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' }, transition: 'all 0.2s ease-in-out' }} onClick={() => handleFamilySelect(family)}>
                      <CardMedia component="img" height="80" image={family.image || ''} alt={family.name || 'Family'} sx={{ objectFit: 'contain', backgroundColor: 'grey.100', p: 1 }} />
                      <CardContent>
                        <Typography variant="h6" gutterBottom>{family.name || 'Unknown Family'}</Typography>
                        <Typography variant="body2" color="text.secondary">{family.description || 'No description available'}</Typography>
                        <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>{family.variants?.length || 0} options available</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))
              ) : (
                <Grid item xs={12}>
                  <Alert severity="warning">
                    <Typography variant="body2">
                      No connectors found for type: <strong>{type}</strong>. Please check the compatibility matrix configuration.
                    </Typography>
                  </Alert>
                </Grid>
              )}
            </Grid>
          ) : (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body1"><strong>{families.find(f => f.id === selectedFamily)?.name || 'Unknown Family'}</strong> - {families.find(f => f.id === selectedFamily)?.description || 'No description available'}</Typography>
                <Typography variant="body2" color="primary" sx={{ cursor: 'pointer', textDecoration: 'underline', mt: 1 }} onClick={() => setSelectedFamily(null)}>
                  ← Back to connector types
                </Typography>
              </Box>
              <Grid container spacing={2}>
                {families.find(f => f.id === selectedFamily)?.variants.map((connector) => {
                  // Use the new compatibility check function
                  const compatibilityResult = checkConnectorCompatibility(connector);
                  const isIncompatible = compatibilityResult?.status === 'incompatible';
                  const hasOtherEnd = (modalOpen === 'A' && connectors.endB?.connector) || (modalOpen === 'B' && connectors.endA?.connector);
                  
                  return (
                    <Grid item xs={12} key={connector.id}>
                      <Card 
                        sx={{ 
                          cursor: isIncompatible ? 'not-allowed' : 'pointer', 
                          opacity: isIncompatible ? 0.6 : 1,
                          border: hasOtherEnd ? (
                            compatibilityResult?.status === 'incompatible' ? '2px solid #f44336' : 
                            compatibilityResult?.status === 'warning' ? '2px solid #ffb300' : 
                            compatibilityResult?.status === 'compatible' ? '2px solid #4caf50' : '1px solid #e0e0e0'
                          ) : '1px solid #e0e0e0',
                          '&:hover': { 
                            boxShadow: isIncompatible ? 1 : 6,
                            transform: isIncompatible ? 'none' : 'translateY(-2px)'
                          },
                          transition: 'all 0.2s ease-in-out'
                        }} 
                        onClick={() => !isIncompatible && handleConnectorSelect(connector)}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
                          <Box sx={{ width: { xs: 36, sm: 48 }, height: { xs: 36, sm: 48 }, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f5f5', borderRadius: 4, p: 0.5, '& img': { width: '100%', height: '100%', objectFit: 'contain' } }}>
                            <ConnectorImageZoom src={connector.image || ''} alt={connector.name || 'Connector'} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                          </Box>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="subtitle1">{connector.name || 'Unknown Connector'}</Typography>
                            <Typography variant="body2" color="text.secondary">{formatGenderWithXlrHint(connector)}</Typography>
                            {isIncompatible && (
                              <Typography variant="caption" color="error.main" sx={{ fontWeight: 'bold' }}>
                                Incompatible with selected connector
                              </Typography>
                            )}
                          </Box>
                          {/* Compatibility indicator */}
                          {hasOtherEnd && (
                            <Box sx={{ ml: 2 }}>
                              <ConnectorCompatibilityIndicator result={compatibilityResult} />
                            </Box>
                          )}
                        </Box>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </>
          )}
        </Box>
      </Dialog>
      
      {/* Tooltip for configuration change notifications */}
      <Tooltip
        open={tooltipOpen}
        anchorEl={tooltipAnchor}
        onClose={() => setTooltipOpen(false)}
        title={tooltipMessage}
        arrow
        placement="top"
        sx={{
          '& .MuiTooltip-tooltip': {
            backgroundColor: '#d32f2f',
            color: 'white',
            fontSize: '0.875rem',
            maxWidth: 300
          }
        }}
      />
    </Box>
  );
}



export default BaseConnectorSelector;